import type { FieldErrors } from 'react-hook-form';

export interface FormErrorSummaryProps {
  /** React Hook Form's `formState.errors`. */
  errors: FieldErrors;
  /**
   * Visible label per field name, in the order the fields are laid out. The
   * ORDER of this map drives the list order, not the order issues landed in
   * `errors` - Zod adds issues as its refinements run, which is neither the
   * reading order of the form nor stable across schema edits.
   *
   * A field with no entry here is skipped rather than shown by its raw name: a
   * summary line reading `coverPhotoFileKey` sends the admin looking for a
   * field with that label, and there isn't one.
   */
  labels: Record<string, string>;
  /** Heading above the list. Required, so no form ships an unlabelled panel. */
  title: string;
}

/**
 * The failed-validation summary for a long form: which fields are wrong, and
 * why, in one place.
 *
 * Why a form needs this on top of per-field errors. In a multi-section,
 * two-column form the offending field is routinely scrolled out of view when
 * the admin hits Save, so a correctly-rendered inline error is invisible and
 * the button reads as broken. That is the exact report this was built for
 * (client feedback 2026-08-20) - the message was rendering, just nowhere the
 * admin was looking.
 *
 * Deliberately NOT a replacement for the inline errors. `Input` and `Textarea`
 * already turn the field's own border red and print the reason beneath it; this
 * says which fields to go and look at. Both, because either alone leaves a
 * case uncovered: the summary cannot point at a field, and the inline error
 * cannot be seen from the bottom of the form.
 *
 * `role="alert"` announces it on appearance, which is what makes a blocked
 * submit perceivable without sight of the form.
 *
 * No link-to-field affordance. Jumping focus needs the form's `setFocus` and
 * an id per row, and nothing has asked for it - the labels name the fields, and
 * the fields are red.
 */
export function FormErrorSummary({ errors, labels, title }: FormErrorSummaryProps) {
  const failed = Object.keys(labels).filter((name) => errors[name]);
  if (failed.length === 0) return null;

  return (
    <div
      role="alert"
      className="border-danger/40 bg-danger/5 rounded-card border p-4 text-sm"
    >
      <p className="text-danger font-semibold">{title}</p>
      <ul className="text-text-secondary mt-2 flex list-disc flex-col gap-1 pl-5">
        {failed.map((name) => (
          <li key={name}>
            <span className="text-text-primary font-medium">{labels[name]}</span>
            {/* The message is the schema's own, so the summary and the field
                below it can never state different reasons for one failure. */}
            {messageOf(errors, name) && <span>: {messageOf(errors, name)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Pure: one field's message, or undefined when the error carries none. */
function messageOf(errors: FieldErrors, name: string): string | undefined {
  const message = errors[name]?.message;
  return typeof message === 'string' ? message : undefined;
}
